/**
 * @fileoverview This component renders the curation page link in the submenu. It is
 * key accessible and has hover and focus states. The refs are populated in
 * the subItemRefs object for keyboard navigation.
 */
import {StyleSheet} from "aphrodite";

import Link from "@khanacademy/wonder-blocks-link";
import {semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import {HeadingXSmall} from "@khanacademy/wonder-blocks-typography";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import {RequestInfo} from "@khan/shared-core";
import {useLocation} from "@khan/urls";

import type {DomainObject} from "../types.ts";

type Props = {
    // The curation page link in the submenu
    item: DomainObject;
    // The ref object for the sub-menu items
    subItemRefs: React.MutableRefObject<object>;
    // The index of this sub-menu item
    index: number;
    // Clicking link should close the learn menu under AppShell.
    onClickLink: () => void;
};

export const SubMenuCurationPageLink = ({
    item,
    subItemRefs,
    index,
    onClickLink,
}: Props) => {
    const publishAnalyticsEvent = usePublishAnalyticsEvent();
    const location = useLocation();

    const handleClick = async () => {
        await publishAnalyticsEvent({
            eventType: "NavBarCourseMenuHeaderClickedTI",
            eventSchemaVersion: 1,
            currentPageUrlPath: location.pathname,
            contentPath: item.href,
            headerId: item.domainId ?? "",
        });
        onClickLink();
    };

    return (
        <Link
            role="menuitem"
            safeWithNav={handleClick}
            href={item.href}
            style={styles.submenuItemTitleLink}
            ref={(node: HTMLAnchorElement) => {
                if (node) {
                    subItemRefs.current[index] = node;
                }
            }}
            tabIndex={-1}
        >
            <HeadingXSmall tag="span">
                {item.translatedTitle.toLocaleUpperCase(RequestInfo.kaLocale)}
            </HeadingXSmall>
        </Link>
    );
};

const styles = StyleSheet.create({
    submenuItemTitleLink: {
        display: "block",
        paddingTop: sizing.size_080,
        paddingBottom: sizing.size_080,
        paddingLeft: sizing.size_120,
        paddingRight: sizing.size_120,
        color: semanticColor.core.foreground.neutral.strong,
    },
});
